import type { IntlShape } from "react-intl";
import { ApiRequestError } from "../../../shared/api/client.ts";
import { invalidRequestReasonMessageId } from "../../../shared/i18n/codes.ts";
import {
  accountLockedMessage,
  conflictReasonMessages,
  edgeRefusedMessage,
  errorMessages,
  loginMessages,
  weakPasswordMessages,
  weakPasswordMinLengthMessage,
} from "../messages.ts";

const MIN_PASSWORD_LENGTH = 12;

/**
 * The one `503` after which something changed: a password reset set the new password, and ending
 * the sessions or recording the reset failed (`details.reason` `password_changed`, spec
 * explorer-access "Session store fails after the password changed"). Never "nothing was changed".
 */
export function isPasswordChangedFailure(error: unknown): boolean {
  return (
    error instanceof ApiRequestError &&
    error.code === "server_error" &&
    error.details?.reason === "password_changed"
  );
}

/** The server refused the password as common or found in breaches (not for length or policy). */
export function isCommonPasswordRefusal(error: unknown): boolean {
  if (!(error instanceof ApiRequestError) || error.code !== "weak_password") return false;
  const rule = error.details?.rule;
  return rule === "common_password" || rule === "compromised_password";
}

/** Readable text for any failure of an Explorer API call; unknown codes get a generic message. */
export function describeError(intl: IntlShape, error: unknown): string {
  if (!(error instanceof ApiRequestError)) return intl.formatMessage(errorMessages.unknown);
  if (isPasswordChangedFailure(error))
    return intl.formatMessage(loginMessages.passwordResetUnfinished);
  switch (error.code) {
    case "account_locked": {
      const seconds = error.details?.locked_for_seconds ?? 60;
      return intl.formatMessage(accountLockedMessage, {
        minutes: Math.max(1, Math.ceil(seconds / 60)),
      });
    }
    case "weak_password": {
      const rule = error.details?.rule;
      if (rule === "min_length") {
        return intl.formatMessage(weakPasswordMinLengthMessage, {
          minLength: error.details?.min_length ?? MIN_PASSWORD_LENGTH,
        });
      }
      return intl.formatMessage(rule ? weakPasswordMessages[rule] : weakPasswordMessages.fallback);
    }
    case "invalid_request": {
      // A domain reason this build knows (e.g. `comment_too_short`) says exactly what to fix.
      const reasonId = error.details?.reason
        ? invalidRequestReasonMessageId(error.details.reason)
        : undefined;
      // eslint-disable-next-line formatjs/enforce-default-message -- generated `invalidRequestReason.*` catalog
      if (reasonId) return intl.formatMessage({ id: reasonId });
      return intl.formatMessage(errorMessages.invalid_request);
    }
    case "conflict": {
      // A stable reason (e.g. `console_user_exists`) says what to do instead of "reload".
      const reason = error.details?.reason;
      if (reason && Object.hasOwn(conflictReasonMessages, reason))
        return intl.formatMessage(
          conflictReasonMessages[reason as keyof typeof conflictReasonMessages],
        );
      return intl.formatMessage(errorMessages.conflict);
    }
    case "invalid_credentials":
    case "invalid_reset_code":
    case "unauthenticated":
    case "sso_failed":
    case "sso_required":
    case "sso_not_available":
    case "invalid_mfa_code":
    case "mfa_challenge_expired":
    case "mfa_reset_pending":
    case "two_factor_required":
    case "forbidden":
    case "csrf_token_invalid":
    case "not_found":
    case "credential_limit_reached":
    case "already_blocked":
    case "block_not_active":
    case "export_not_ready":
    case "export_limit_reached":
    case "export_expired":
    case "invitation_used":
    case "invitation_expired":
    case "invitation_revoked":
    case "last_administrator":
    case "own_account_protected":
    case "last_platform_administrator":
    case "identity_provider_not_tested":
    case "operator_deactivated":
    case "rate_limited":
    case "step_up_rate_limited":
    case "server_error":
    case "network_error":
      return intl.formatMessage(errorMessages[error.code]);
    case "unknown_error":
      // The API always answers in the contract shape; a bare 403 comes from the edge firewall.
      if (error.status === 403) return intl.formatMessage(edgeRefusedMessage);
      return intl.formatMessage(errorMessages.unknown);
    case "invalid_response":
      return intl.formatMessage(errorMessages.unknown);
  }
}

/** Error code of a failure, when it came from the API. */
export function errorCode(error: unknown): string | undefined {
  return error instanceof ApiRequestError ? error.code : undefined;
}
